// @flow

import {
  SET_SELECTION,
  OPEN_PRODUCT, CLOSE_PRODUCT, SELECT_IMAGE,
  TOGGLE_ROOM, SET_PAGE,
  SHOW_ALL, ORDER_DATA_GET_REQUEST,
  ORDER_DATA_GET_SUCCESS, ORDER_DATA_GET_FAILURE,
  ORDER_ROW_CREATE_REQUEST, ORDER_ROW_CREATE_SUCCESS,
  ORDER_ROW_CREATE_FAILURE, ORDER_ROW_UPDATE_REQUEST,
  ORDER_ROW_UPDATE_SUCCESS, ORDER_ROW_UPDATE_FAILURE,
  ORDER_ROW_REMOVE_REQUEST, ORDER_ROW_REMOVE_SUCCESS,
  ORDER_ROW_REMOVE_FAILURE, ORDER_ROWS_UPDATE_REQUEST,
  ORDER_ROWS_UPDATE_SUCCESS, ORDER_ROWS_UPDATE_FAILURE,
  SEND_EMAIL_SUCCESS, SEND_EMAIL_FAILURE,
  TOGGLE_MENU, CLEAR, TOGGLE_HELP,
  SET_ADVANCED_SELECTION, SELECT_REQUEST,
  SELECT_FAILURE, SET_ADDITIONAL_SELECTION,
  ORDER_ROWS_CLEAR_REQUEST, ORDER_ROWS_CLEAR_SUCCESS,
  ORDER_ROWS_CLEAR_FAILURE, TOGGLE_ROOM_PRICE,
  TOGGLE_ADDITIONAL_PRICE, TOGGLE_PRICES,
} from '../actions/customerOrderView';
import {UPDATE_SUCCESS} from '../actions/customerView';
import _ from 'lodash/fp';
import type {Exact} from './configTemplateView';
import type {
  OrderRowDto,
  ProductSlotDto,
  ProductSlotConfigDto,
} from '../dtos';
import uuid from 'uuid';
import {
  sortRoomList,
} from './configTemplateView';
import {
  calculateOrderCompleteState,
} from './orderView';

type ActiveSelectionState = {
  additional: bool,
  room: ?any,
  slot: ?any,
  orderRow: ?any,
  config: ?any,
  parentConfig: ?any,
  canChoose: bool,
};

type OrderRowsBySlotIdState = {
  [key: string]: any,
};

type OrderRowsState = {
  bySlotId: OrderRowsBySlotIdState,
  isFetching: bool,
  error: ?any,
};

type ExtraRowsState = {
  isFetching: bool,
  list: any[],
};

type CustomRowsState = {
  isFetching: bool,
  list: any[],
};

type PaginationState = {
  page: number,
  perPage: number,
  showAll: bool,
};

type ProductState = {
  activeImage: ?any,
  activeProduct: ?any,
  activeVariant: ?any,
  show: bool,
};

type ProductsState = {
  isFetching: bool,
  list: any[],
  error: ?any,
  products: any[],
};

type AdditionalPriceState = {
  open: boolean,
  price: number,
};

type PricesState = {
  roomPrices: any[],
  additionalTotalPrice: AdditionalPriceState,
  discount: number,
  total: number,
  open: boolean,
};

type RoomsExpandedState = {
  [key: string]: bool,
};

type RoomsState = {
  expanded: RoomsExpandedState,
  list: any[],
  isFetching: bool,
};

export type State_ = {
  activeSelection: ActiveSelectionState,
  help: bool,
  menuVisibility: bool,
  orderComplete: bool,
  orderRows: OrderRowsState,
  extraRows: ExtraRowsState,
  customRows: CustomRowsState,
  pagination: PaginationState,
  product: ProductState,
  products: ProductsState,
  prices: PricesState,
  rooms: RoomsState,
};

type State = {
  activeSelection: Exact<ActiveSelectionState>,
  help: bool,
  menuVisibility: bool,
  orderComplete: bool,
  orderRows: Exact<OrderRowsState>,
  extraRows: Exact<ExtraRowsState>,
  customRows: Exact<CustomRowsState>,
  pagination: Exact<PaginationState>,
  product: Exact<ProductState>,
  products: Exact<ProductsState>,
  prices: Exact<PricesState>,
  rooms: Exact<RoomsState>,
};

const getProductSlotsPrice = (rowsBySlotId, productSlots) =>
  _.flow(
    _.flatMap(
      slot => _.flow(
        _.pickBy((x, k: string) => _.startsWith(`${slot.id}_`, k)),
        _.values,
        _.map(existingRow => ({
          id: existingRow.id,
          name: `${existingRow.productName} - ${existingRow.productVariantName}`,
          totalPrice: (!_.isNil(existingRow.orderedPrice) &&
          !_.isNil(existingRow.orderedQuantity)
            ? existingRow.orderedPrice * existingRow.orderedQuantity
            : existingRow.price * existingRow.quantity),
        })),
      )(rowsBySlotId),
    ),
    _.sortBy([x => _.toLower(x.name)]),
  )(productSlots);

const getTotalExtraRowPriceByRoomId = (roomId, extraRows) => {
  const currentExtraRows = _.filter({roomId}, extraRows);
  return _.sumBy(extraRow => {
    const isOrdered = !_.isNil(extraRow.orderedPrice);
    const price = isOrdered ? extraRow.orderedPrice : extraRow.price;
    return price * extraRow.quantity;
  }, currentExtraRows);
};

const getRoomPrice = (room, bySlotId, extraRows) => {
  const orderRows = getProductSlotsPrice(bySlotId, room.productSlots);
  const totalExtraPrice = getTotalExtraRowPriceByRoomId(room.id, extraRows);
  return {
    orderRows,
    totalExtraPrice,
    totalPrice: _.sumBy(x => x.totalPrice, orderRows) + totalExtraPrice,
  };
};

const getOrderPrice = (rooms, bySlotId, extraRows, discount, customRows = []) => {
  const roomPrices = _.reduce((roomPrices, room) => ({
    ...roomPrices,
    [room.id]: getRoomPrice(room, bySlotId, extraRows),
  }), {}, rooms);

  const roomTotalPrice = _.flow(
    _.values,
    _.map(x => x.totalPrice),
    _.sum
  )(roomPrices);
  const additionalTotalPrice = _.sumBy(x => x.totalSellingPrice, customRows);
  const totalPrice = Math.round(roomTotalPrice + additionalTotalPrice);
  const finalPrice = Math.round(Math.max(totalPrice - discount, 0));
  return {
    roomPrices,
    additionalTotalPrice: {
      open: false,
      price: additionalTotalPrice,
    },
    totalPrice: finalPrice,
  };
};

const formatRoomPrices = pricesByRoomId =>
  _.map(({id, name}) => ({
    id,
    name,
    open: false,
    prices: pricesByRoomId[id],
  }));

const values: (x: {
  [key: number]: ProductSlotConfigDto[],
}) => ProductSlotConfigDto[][] = _.values;

const buildConfigTree = (slot: ProductSlotDto, config: ProductSlotConfigDto[], orderRows: OrderRowDto[], parents: $Shape<ProductSlotConfigDto>[] = [{id: -1}]) =>
  _.flow(
    _.map(c => ({
      config: c,
      parent: _.find({id: c.parentId || -1}, parents),
    })),
    _.filter(x =>
      !!x.parent && (
        x.parent.id === -1 ||
        _.some({id: x.config.productVariant.id}, x.parent.productVariant.related) ||
        !!_.find({
          productSlotId: x.config.productSlotId,
          parentConfigId: x.parent.id,
          productTypeId: x.config.productType.id,
          configId: x.config.id,
        }, orderRows)
      )
    ),
    _.map(x => ({
      ...x.config,
      productType: !!x.parent && x.parent.id === -1 && x.config.productType.id !== slot.productTypeId
        ? {
          id: slot.productTypeId,
          name: slot.name,
          archived: null,
        }
        : x.config.productType,
    })),
    _.groupBy(x => x.productType.id),
    values,
    _.sortBy([([{productType: {name}}, ...tail]) => name]),
    _.map(([head, ...tail]) => ({
      ...head.productType,
      uuid: uuid.v4(),
      configs: [head, ...tail],
      children: buildConfigTree(slot, config, orderRows, [head, ...tail]),
    })),
  )(config);

const mapConfigTree = orderRows =>
  _.map(r => ({
    ...r,
    productSlots: _.map(s => ({
      ...s,
      configTree: !!s.advanced
        ? buildConfigTree(s, s.config, orderRows)
        : s.config,
    }))(r.productSlots),
  }));

const calculateIsOptional = (productSlots): bool =>
  !_.isEmpty(productSlots) &&
  _.every(p =>
    !p.advanced
      ? !p.defaultProductId
      : (
        !p.standardProductId &&
        _.every(c => _.isEmpty(c.standardProducts), p.config)
      )
    , productSlots);

const calculateAdvancedComplete = (bySlotId, slotId, productType, standardProductId, parentId) => {
  const orderRow = bySlotId[`${slotId}_${parentId || ''}_${productType.id}`];
  if (!!standardProductId && !orderRow) {
    return false;
  }
  if (!!orderRow) {
    const config = _.find({id: orderRow.configId}, productType.configs);
    if (!!config.standardProducts.length) {
      const requiredChildren = _.flow(
        _.map(x => ({
          type: x,
          standardProduct: _.find({productTypeId: x.id}, config.standardProducts),
        })),
        _.filter(x => !!x.standardProduct && _.some({productTypeId: x.standardProduct.productTypeId}, config.productVariant.related)),
      )(productType.children);
      return !requiredChildren.length || _.every(x =>
        calculateAdvancedComplete(bySlotId, slotId, x.type, x.standardProduct.productId, config.id), requiredChildren);
    }
  }
  return true;
};

const calculateIsComplete = (bySlotId, isOptional, productSlots): bool =>
  _.every(slot => {
    if (!!slot.advanced && !!slot.configTree.length) {
      return calculateAdvancedComplete(bySlotId, slot.id, slot.configTree[0], slot.standardProductId, null);
    } else {
      return !!bySlotId[`${slot.id}__`] || !!isOptional || !slot.defaultProductId;
    }
  }, productSlots);

const mapStatusFlags = bySlotId =>
  _.map(r => {
    const isOptional = calculateIsOptional(r.productSlots);
    const isComplete = calculateIsComplete(bySlotId, isOptional, r.productSlots);
    return {
      ...r,
      isComplete,
      isOptional,
    };
  });

export const groupSlots: any[] => OrderRowsBySlotIdState =
  _.keyBy(r => `${r.productSlotId}_${r.parentConfigId || ''}_${r.productTypeId || ''}`);

const defaultActiveSelectionState = {
  room: null,
  slot: null,
  orderRow: null,
  config: null,
  parentConfig: null,
  canChoose: false,
  additional: false,
};

const defaultOrderRows = {
  bySlotId: {},
  isFetching: false,
  error: null,
};

const defaultExtraOrderRows = {
  list: [],
  isFetching: false,
};

const defaultCustomOrderRows = {
  list: [],
  isFetching: false,
};

const defaultPagination = {
  page: 1,
  perPage: 12,
  showAll: true,
};

const defaultProductState = {
  activeImage: null,
  activeProduct: null,
  activeVariant: null,
  show: false,
};

const defaultProductsState = {
  isFetching: false,
  list: [],
  error: null,
  products: [],
};

const defaultAdditionalPriceState = {
  open: false,
  price: 0,
};

const defaultPrices = {
  roomPrices: [],
  additionalTotalPrice: defaultAdditionalPriceState,
  discount: 0,
  total: 0,
  open: false,
};

const defaultRoomsState = {
  expanded: {},
  list: [],
  isFetching: false,
};

const defaultCustomerOrderViewState = {
  activeSelection: defaultActiveSelectionState,
  help: false,
  menuVisibility: true,
  orderComplete: false,
  orderRows: defaultOrderRows,
  extraRows: defaultExtraOrderRows,
  customRows: defaultCustomOrderRows,
  pagination: defaultPagination,
  product: defaultProductState,
  products: defaultProductsState,
  prices: defaultPrices,
  rooms: defaultRoomsState,
};

export const getParentConfig = (bySlotId: OrderRowsBySlotIdState, productType: ?any, slot: ?any): ?any => {
  if (!!productType && !!slot) {
    const parentIds = _.flow(
      _.map('parentId'),
      _.uniq,
    )(productType.configs);
    const parentRow = _.find(r => _.includes(r.configId, parentIds), bySlotId);
    if (!!parentRow) {
      return _.find({id: parentRow.configId}, slot.config);
    }
  }
  return null;
};

const getCurrentRow = (rows: any[], activeSelection: ActiveSelectionState): ?any => {
  const {slot, config} = activeSelection;
  return !!slot
    ? !!config
      ? _.find(r =>
        r.productSlotId === slot.id &&
        _.some({id: r.configId}, config.configs), rows)
      : _.find({productSlotId: slot.id}, rows)
    : null;
};

const sortIdToTop = id =>
  _.sortBy([i => i.id !== id]);

const sortIdsToTop = ids =>
  _.sortBy([({id}) => !_.includes(id, ids)]);

const sortByMainVariantItemNumber =
  _.sortBy([i => {
    const mainVariant = _.find({id: i.mainVariantId}, i.variants);
    return mainVariant ? _.toLower(mainVariant.itemNumber) : '';
  }]);

const sortByItemNumber =
  _.sortBy([i => _.toLower(i.itemNumber)]);

const sortWithConfig = (parentConfig, type) =>
  _.sortBy([x =>
    !type || !_.find({
      parentId: !!parentConfig
        ? parentConfig.id
        : null,
      productVariantId: x.variant.id,
    }, type.configs)]);

const sortAndMapVariants = (
  products: any[],
  slot: any,
  productType: ?any,
  parentConfig: ?any,
  orderRow: ?any,
): any[] => {
  const type = productType;
  return _.flow(
    sortByMainVariantItemNumber,
    !!slot.advanced ?
      !!parentConfig
        ? sortIdsToTop(_.map('productId', parentConfig.standardProducts))
        : sortIdToTop(slot.standardProductId)
      : sortIdToTop(slot.defaultProductId),
    _.map(p => ({
      ...p,
      variants: _.flow(
        sortByItemNumber,
        sortIdToTop(p.mainVariantId)
      )(p.variants),
    })),
    _.flatMap(product =>
      _.map(variant => ({variant, product}), product.variants)
    ),
    _.filter(_.overEvery([
      ({variant}) => !slot.advanced || !type || _.some({productVariantId: variant.id}, type.configs),
      ({product, variant}) => {
        if ((!product.archived && !variant.archived) || !!variant.replacement) {
          return true;
        }
        if (!!orderRow) {
          if (!!orderRow.advanced && !!type) {
            const rowConfig = _.find({id: orderRow.configId}, type.configs);
            return !!rowConfig && rowConfig.productVariantId === variant.id;
          } else if (!orderRow.advanced && orderRow.productVariantId === variant.id) {
            return true;
          }
        }
        return false;
      },
    ])),
    !!slot.advanced
      ? sortWithConfig(parentConfig, type)
      : _.identity,
  )(products);
};

const closePrices = prices => ({
  open: false,
  roomPrices: _.map(x => ({
    ...x,
    open: false,
  }), prices.roomPrices),
  additionalTotalPrice: {
    ...prices.additionalTotalPrice,
    open: false,
  },
});

const customerOrderView = (state: State_ = defaultCustomerOrderViewState, action: any): State => {
  switch (action.type) {
    case SELECT_REQUEST: {
      const orderRow = !action.productType
        ? state.orderRows.bySlotId[`${action.slot.id}__`]
        : _.find(r => r.productSlotId === action.slot.id && _.some({id: r.configId}, action.productType.configs), state.orderRows.bySlotId);
      const parentConfig = getParentConfig(state.orderRows.bySlotId, action.productType, action.slot);
      const canChoose = !!parentConfig
        || (!!action.productType && _.some({parentId: null}, action.productType.configs));
      return {
        ...state,
        activeSelection: {
          ...state.activeSelection,
          orderRow,
          room: action.room,
          slot: action.slot,
          config: action.productType || null,
          parentConfig,
          canChoose,
          additional: false,
        },
        products: {
          ...state.products,
          error: null,
          isFetching: true,
          list: [],
          products: [],
        },
        prices: {
          ...state.prices,
          ...closePrices(state.prices),
        },
      };
    }
    case SELECT_FAILURE:
      return {
        ...state,
        products: {
          ...state.products,
          error: action.error,
          isFetching: false,
        },
      };
    case SET_SELECTION:
    case SET_ADVANCED_SELECTION:
      return {
        ...state,
        menuVisibility: false,
        pagination: defaultPagination,
        products: {
          ...state.products,
          error: null,
          isFetching: false,
          list: sortAndMapVariants(
            action.products,
            action.slot,
            state.activeSelection.config,
            state.activeSelection.parentConfig,
            state.activeSelection.orderRow,
          ),
          products: action.products,
        },
      };
    case SET_ADDITIONAL_SELECTION:
      return {
        ...state,
        menuVisibility: false,
        activeSelection: {
          ...defaultActiveSelectionState,
          additional: true,
        },
      };
    case OPEN_PRODUCT:
      return {
        ...state,
        product: {
          ...state.product,
          activeImage: _.find('isDefault', action.variant.images) || _.head(action.variant.images) || null,
          activeProduct: action.product || null,
          activeVariant: action.variant || null,
          show: true,
        },
        prices: {
          ...state.prices,
          ...closePrices(state.prices),
        },
      };
    case CLOSE_PRODUCT:
      return {
        ...state,
        product: {
          ...state.product,
          activeProduct: null,
          activeVariant: null,
          show: false,
        },
      };
    case SELECT_IMAGE:
      return {
        ...state,
        product: {
          ...state.product,
          activeImage: action.image,
        },
      };
    case TOGGLE_ROOM:
      return {
        ...state,
        rooms: {
          ...state.rooms,
          expanded: {
            [action.roomId]: action.expanded,
          },
        },
        prices: {
          ...state.prices,
          ...closePrices(state.prices),
        },
      };
    case SET_PAGE:
      return {
        ...state,
        pagination: {
          ...state.pagination,
          page: action.page,
        },
        prices: {
          ...state.prices,
          ...closePrices(state.prices),
        },
      };
    case SHOW_ALL:
      return {
        ...state,
        pagination: {
          ...state.pagination,
          showAll: true,
        },
        prices: {
          ...state.prices,
          ...closePrices(state.prices),
        },
      };
    case ORDER_DATA_GET_REQUEST: {
      const list = [];
      const orderPrice = getOrderPrice(list, state.orderRows.bySlotId, state.extraRows.list, action.order.discount);
      return {
        ...state,
        orderComplete: false,
        orderRows: {
          ...state.orderRows,
          error: null,
          isFetching: true,
        },
        rooms: {
          ...state.rooms,
          isFetching: true,
          list,
        },
        prices: {
          ...state.prices,
          open: false,
          discount: action.order.discount,
          roomPrices: formatRoomPrices(orderPrice.roomPrices)(list),
          additionalTotalPrice: orderPrice.additionalTotalPrice,
          total: orderPrice.totalPrice,
        },
      };
    }
    case ORDER_DATA_GET_SUCCESS: {
      const bySlotId = groupSlots(action.orderRows);
      const list = _.flow(
        sortRoomList,
        mapConfigTree(action.orderRows),
        mapStatusFlags(bySlotId),
      )(action.apartmentConfig.rooms);
      const orderPrice = getOrderPrice(list, bySlotId, action.extraRows, state.prices.discount, action.customRows);
      return {
        ...state,
        orderComplete: calculateOrderCompleteState(bySlotId, list, action.orderRows),
        customRows: {
          ...state.customRows,
          isFetching: false,
          list: action.customRows,
        },
        extraRows: {
          ...state.extraRows,
          isFetching: false,
          list: action.extraRows,
        },
        orderRows: {
          ...state.orderRows,
          bySlotId,
          error: null,
          isFetching: false,
        },
        rooms: {
          ...state.rooms,
          isFetching: false,
          list,
        },
        prices: {
          ...state.prices,
          roomPrices: formatRoomPrices(orderPrice.roomPrices)(list),
          additionalTotalPrice: orderPrice.additionalTotalPrice,
          total: orderPrice.totalPrice,
        },
      };
    }
    case ORDER_DATA_GET_FAILURE:
      return {
        ...state,
        orderRows: {
          ...state.orderRows,
          error: action.error,
          isFetching: false,
        },
        rooms: {
          ...state.rooms,
          isFetching: false,
        },
      };
    case ORDER_ROW_CREATE_REQUEST:
    case ORDER_ROW_UPDATE_REQUEST:
    case ORDER_ROW_REMOVE_REQUEST:
    case ORDER_ROWS_UPDATE_REQUEST:
    case ORDER_ROWS_CLEAR_REQUEST:
      return {
        ...state,
        orderRows: {
          ...state.orderRows,
          error: null,
          isFetching: true,
        },
        prices: {
          ...state.prices,
          ...closePrices(state.prices),
        },
      };
    case ORDER_ROW_CREATE_SUCCESS:
    case ORDER_ROW_UPDATE_SUCCESS:
    case ORDER_ROW_REMOVE_SUCCESS:
    case ORDER_ROWS_UPDATE_SUCCESS: {
      const bySlotId = groupSlots(action.rows);
      const list = mapStatusFlags(bySlotId)(state.rooms.list);
      const orderPrice = getOrderPrice(list, bySlotId, state.extraRows.list, state.prices.discount, state.customRows.list);
      const parentConfig = getParentConfig(bySlotId, state.activeSelection.config, state.activeSelection.slot);
      const canChoose = !!parentConfig
        || (!!state.activeSelection.config && _.some({parentId: null}, state.activeSelection.config.configs));
      const orderRow = action.row || getCurrentRow(action.rows, state.activeSelection);
      return {
        ...state,
        activeSelection: {
          ...state.activeSelection,
          orderRow,
          parentConfig,
          canChoose,
        },
        orderComplete: calculateOrderCompleteState(bySlotId, list, action.rows),
        orderRows: {
          ...state.orderRows,
          bySlotId,
          error: null,
          isFetching: false,
        },
        rooms: {
          ...state.rooms,
          isFetching: false,
          list,
        },
        prices: {
          ...state.prices,
          roomPrices: formatRoomPrices(orderPrice.roomPrices)(list),
          additionalTotalPrice: orderPrice.additionalTotalPrice,
          total: orderPrice.totalPrice,
        },
        product: {
          ...state.product,
          activeProduct: null,
          activeVariant: null,
          show: false,
        },
        products: {
          ...state.products,
          list: sortAndMapVariants(
            state.products.products,
            state.activeSelection.slot || {},
            state.activeSelection.config,
            parentConfig,
            orderRow,
          ),
        },
      };
    }
    case ORDER_ROWS_CLEAR_SUCCESS: {
      const rowsById = defaultOrderRows.bySlotId;
      const extraRows = [];
      const list = mapStatusFlags(rowsById)(state.rooms.list);
      const orderPrice = getOrderPrice(list, rowsById, extraRows, state.prices.discount, state.customRows.list);
      return {
        ...state,
        extraRows: defaultExtraOrderRows,
        activeSelection: defaultActiveSelectionState,
        orderComplete: calculateOrderCompleteState(rowsById, list, []),
        orderRows: defaultOrderRows,
        rooms: {
          ...state.rooms,
          expanded: defaultRoomsState.expanded,
          isFetching: false,
          list,
        },
        prices: {
          ...state.prices,
          roomPrices: formatRoomPrices(orderPrice.roomPrices)(list),
          additionalTotalPrice: orderPrice.additionalTotalPrice,
          total: orderPrice.totalPrice,
        },
        product: defaultProductState,
        products: defaultProductsState,
      };
    }
    case ORDER_ROW_CREATE_FAILURE:
    case ORDER_ROW_UPDATE_FAILURE:
    case ORDER_ROW_REMOVE_FAILURE:
    case ORDER_ROWS_UPDATE_FAILURE:
    case ORDER_ROWS_CLEAR_FAILURE:
      return {
        ...state,
        orderRows: {
          ...state.orderRows,
          error: action.error,
          isFetching: false,
        },
      };
    case SEND_EMAIL_SUCCESS:
    case SEND_EMAIL_FAILURE:
      return {
        ...state,
        help: false,
      };
    case TOGGLE_MENU:
      return {
        ...state,
        menuVisibility: !!action.show,
      };
    case TOGGLE_HELP:
      return {
        ...state,
        help: !!action.show,
        prices: {
          ...state.prices,
          ...closePrices(state.prices),
        },
      };
    case UPDATE_SUCCESS: {
      const bySlotId = groupSlots(action.order.rows);
      const list = mapStatusFlags(bySlotId)(state.rooms.list);
      const orderPrice = getOrderPrice(list, bySlotId, state.extraRows.list, state.prices.discount, state.customRows.list);
      return {
        ...state,
        activeSelection: defaultActiveSelectionState,
        orderComplete: calculateOrderCompleteState(bySlotId, list, action.order.rows),
        orderRows: {
          ...state.orderRows,
          bySlotId,
        },
        rooms: {
          ...state.rooms,
          list,
        },
        prices: {
          ...state.prices,
          open: false,
          roomPrices: formatRoomPrices(orderPrice.roomPrices)(list),
          additionalTotalPrice: orderPrice.additionalTotalPrice,
          total: orderPrice.totalPrice,
        },
        products: {
          ...state.products,
          list: [],
          products: [],
        },
      };
    }
    case TOGGLE_ROOM_PRICE:
      return {
        ...state,
        prices: {
          ...state.prices,
          roomPrices: _.map(x => ({
            ...x,
            open: x.id === action.roomId && !x.open,
          }), state.prices.roomPrices),
          additionalTotalPrice: {
            ...state.prices.additionalTotalPrice,
            open: false,
          },
        },
      };
    case TOGGLE_ADDITIONAL_PRICE:
      return {
        ...state,
        prices: {
          ...state.prices,
          roomPrices: _.map(x => ({
            ...x,
            open: false,
          }), state.prices.roomPrices),
          additionalTotalPrice: {
            ...state.prices.additionalTotalPrice,
            open: !state.prices.additionalTotalPrice.open,
          },
        },
      };
    case TOGGLE_PRICES:
      return {
        ...state,
        prices: {
          ...state.prices,
          ...(!!state.prices.open
            ? closePrices(state.prices)
            : {open: true}
          ),
        },
      };
    case CLEAR:
      return {
        ...state,
        activeSelection: defaultActiveSelectionState,
        menuVisibility: true,
        orderComplete: false,
        orderRows: defaultOrderRows,
        pagination: defaultPagination,
        product: defaultProductState,
        products: defaultProductsState,
        prices: defaultPrices,
        rooms: defaultRoomsState,
      };
    default:
      return state;
  }
};

export default customerOrderView;



// WEBPACK FOOTER //
// ./src/reducers/customerOrderView.js